import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { RichTextEditor, Link } from "@mantine/tiptap";
import Highlight from "@tiptap/extension-highlight";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import ColorExt from "@tiptap/extension-color";
import { TextStyle } from "@tiptap/extension-text-style";
import Image from "@tiptap/extension-image";
import { Group, Text, Box } from "@mantine/core";
import { useEffect, useCallback } from "react";
import { IconPhoto } from "@tabler/icons-react";

interface EditorProps {
  value: string;
  label?: string;
  required?: boolean;
  disabled: boolean;
  maxLength: number;
  onChange: (htmlContent: string, textContent: string) => void;
}

export function Editor({
  value,
  onChange,
  disabled,
  maxLength,
  label,
  required,
}: EditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link,
      Highlight,
      ColorExt,
      TextStyle,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Image.configure({
        inline: true, // Allow images to be inline with text
        allowBase64: true, // Allow images to be stored as Base64 data URLs
        HTMLAttributes: {
          style: "max-width: 100%; height: auto; cursor: pointer;", // Make images responsive and clickable
        },
      }),
    ],
    content: value,
    onUpdate: ({ editor }) => {
      if (!disabled) {
        const h = editor.getHTML();
        const t = editor.getText();
        if (!t || t.length <= maxLength) onChange(h, t);
      }
    },
    editorProps: {
      handleDOMEvents: {
        // Handle double-click on images to edit URL
        dblclick: (view, event) => {
          const target = event.target as HTMLElement;
          if (target.tagName === "IMG") {
            event.preventDefault();
            const currentSrc = target.getAttribute("src");
            const newUrl = window.prompt("Edit image URL:", currentSrc || "");

            if (newUrl !== null && newUrl !== currentSrc && editor) {
              // Find the position of the clicked image
              const pos = view.posAtDOM(target, 0);
              const { schema } = view.state;
              const node = schema.nodes.image.create({ src: newUrl });
              const transaction = view.state.tr.replaceWith(pos, pos + 1, node);
              view.dispatch(transaction);
            }
            return true;
          }
          return false;
        },
      },
      handleDrop: (view, event, _slice, moved) => {
        // Prevent default behavior if an image is dropped from outside the editor
        if (
          !moved &&
          event.dataTransfer &&
          event.dataTransfer.files.length > 0
        ) {
          const file = event.dataTransfer.files[0];

          if (file.type.startsWith("image/")) {
            const reader = new FileReader();
            reader.onload = (readerEvent) => {
              const src = readerEvent.target?.result as string;

              // Get the position where the image was dropped
              const coordinates = view.posAtCoords({
                left: event.clientX,
                top: event.clientY,
              });
              if (coordinates) {
                const { schema } = view.state;
                const node = schema.nodes.image.create({ src });
                const transaction = view.state.tr.insert(coordinates.pos, node);
                view.dispatch(transaction);
              }
            };
            reader.readAsDataURL(file); // Read file as Base64
            return true; // Indicate that we handled the drop
          }
        }
        return false; // Let Tiptap/ProseMirror handle other drop events
      },
      handlePaste: (view, event, _slice) => {
        const items = Array.from(event.clipboardData?.items || []);
        for (const item of items) {
          if (item.type.startsWith("image/")) {
            const file = item.getAsFile();
            if (file) {
              const reader = new FileReader();
              reader.onload = (readerEvent) => {
                const src = readerEvent.target?.result as string;
                const { schema } = view.state;
                const node = schema.nodes.image.create({ src });
                const transaction = view.state.tr.replaceSelectionWith(node);
                view.dispatch(transaction);
              };
              reader.readAsDataURL(file);
              return true; // Handled
            }
          }
        }
        return false; // Not handled
      },
    },
  });

  // Sync the value prop with the editor content when it changes
  useEffect(() => {
    if (editor && editor.getHTML() !== value) {
      editor.commands.setContent(value);
    }
  }, [editor, value]);

  // Function to handle adding image from URL
  const addImageFromUrl = useCallback(() => {
    if (!editor) return;

    // Check if an image is currently selected
    const { state } = editor;
    const { selection } = state;
    let currentImageUrl = "";

    // Try to get the current image URL if an image is selected
    state.doc.nodesBetween(selection.from, selection.to, (node) => {
      if (node.type.name === "image") {
        currentImageUrl = node.attrs.src || "";
      }
    });

    const promptMessage = currentImageUrl
      ? "Edit image URL"
      : "Enter image URL\n\n" +
        "Note: The URL must be a direct link to an image file.\n" +
        "For example, Google Photos sharing links may not work.";

    const url = window.prompt(promptMessage, currentImageUrl);

    if (url !== null && url !== currentImageUrl) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  }, [editor]);

  const customColors = [
    "#228BE6", // Mantine primary color
    "#2F855A", // A nice green
    "#E03131", // A vibrant red
    "#F59E0B", // A warm yellow
    "#868E96", // A neutral gray
    "#FFFFFF", // White
    "#000000", // Black
  ];

  return (
    <>
      <style>{`
        .ProseMirror img {
          max-width: 100%;
          height: auto;
          cursor: pointer;
          border: 1px solid #dee2e6;
          border-radius: 4px;
          vertical-align: middle;
          margin: 0 2px;
        }
        .ProseMirror img:hover {
          border-color: #228BE6;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
      `}</style>
      {label && (
        <Group gap={3}>
          <Text size="sm" fw={500} mb={3}>
            {label}
          </Text>
          {required && <Text c="rgb(224, 49, 49)">*</Text>}
        </Group>
      )}
      <Box
        style={
          {
            "& .ProseMirror img": {
              maxWidth: "100%",
              height: "auto",
              display: "block",
              margin: "10px 0",
              cursor: "pointer",
              border: "1px solid #dee2e6",
              borderRadius: "4px",
            },
          } as any
        }
      >
        <RichTextEditor editor={editor}>
          <RichTextEditor.Toolbar sticky stickyOffset={60}>
            <RichTextEditor.ControlsGroup>
              <RichTextEditor.Bold />
              <RichTextEditor.Italic />
              <RichTextEditor.Underline />
              <RichTextEditor.Strikethrough />
              <RichTextEditor.ClearFormatting />
              <RichTextEditor.Highlight />
              <RichTextEditor.Code />
            </RichTextEditor.ControlsGroup>

            <RichTextEditor.ControlsGroup>
              <RichTextEditor.H1 />
              <RichTextEditor.H2 />
              <RichTextEditor.H3 />
              <RichTextEditor.H4 />
            </RichTextEditor.ControlsGroup>

            <RichTextEditor.ControlsGroup>
              <RichTextEditor.Blockquote />
              <RichTextEditor.Hr />
              <RichTextEditor.BulletList />
              <RichTextEditor.OrderedList />
            </RichTextEditor.ControlsGroup>

            <RichTextEditor.ControlsGroup>
              <RichTextEditor.Link />
              <RichTextEditor.Unlink />
            </RichTextEditor.ControlsGroup>

            <RichTextEditor.ControlsGroup>
              <RichTextEditor.Control
                onClick={addImageFromUrl}
                aria-label="Insert image from URL"
                title="Insert image from URL"
              >
                <IconPhoto size={16} stroke={1.5} />
              </RichTextEditor.Control>
            </RichTextEditor.ControlsGroup>

            <RichTextEditor.ControlsGroup>
              <RichTextEditor.AlignLeft />
              <RichTextEditor.AlignCenter />
              <RichTextEditor.AlignJustify />
              <RichTextEditor.AlignRight />
            </RichTextEditor.ControlsGroup>

            <RichTextEditor.ControlsGroup>
              <RichTextEditor.ColorPicker colors={customColors} />
              <RichTextEditor.UnsetColor />
            </RichTextEditor.ControlsGroup>

            <RichTextEditor.ControlsGroup>
              <RichTextEditor.Undo />
              <RichTextEditor.Redo />
            </RichTextEditor.ControlsGroup>
          </RichTextEditor.Toolbar>

          <RichTextEditor.Content
            style={
              {
                "& img": {
                  maxWidth: "100%",
                  height: "auto",
                  display: "block",
                  margin: "10px 0",
                  cursor: "pointer",
                  border: "1px solid #e0e0e0",
                  borderRadius: "4px",
                },
              } as any
            }
          />
        </RichTextEditor>
      </Box>
    </>
  );
}
